import { FC, useState, useEffect, useRef } from "react";
import Select, { SelectProps } from "@cloudscape-design/components/select";
import Spinner from "@cloudscape-design/components/spinner";
import Icon from "@cloudscape-design/components/icon";

interface InlineSelectFieldProps {
    label: string;
    value: string;
    options: SelectProps.Option[];
    onSave: (newValue: string) => Promise<void>;
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
}

const InlineSelectField: FC<InlineSelectFieldProps> = ({
    label,
    value,
    options,
    onSave,
    disabled,
    loading,
    placeholder = "Select an option",
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedOption, setSelectedOption] = useState<SelectProps.Option | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const match = options.find((opt) => opt.value === value);
        setSelectedOption(match || null);
    }, [value, options]);

    useEffect(() => {
        if (!isEditing) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                cancelEdit();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isEditing]);

    const enterEditMode = () => {
        if (disabled || isSaving) return;
        setIsEditing(true);
    };

    const cancelEdit = () => {
        const match = options.find((opt) => opt.value === value);
        setSelectedOption(match || null);
        setIsEditing(false);
    };

    const handleChange: SelectProps["onChange"] = async ({ detail }) => {
        const selected = detail.selectedOption;
        setSelectedOption(selected);

        const newValue = selected.value || "";
        if (newValue === value) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            await onSave(newValue);
            setIsEditing(false);
        } catch {
            const match = options.find((opt) => opt.value === value);
            setSelectedOption(match || null);
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    if (isEditing) {
        return (
            <div ref={containerRef} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ fontWeight: "bold", minWidth: "120px" }}>{label}</label>
                <div style={{ minWidth: "200px", flex: 1 }}>
                    <Select
                        selectedOption={selectedOption}
                        onChange={handleChange}
                        options={options}
                        placeholder={placeholder}
                        disabled={isSaving}
                        filteringType="auto"
                        statusType={loading ? "loading" : "finished"}
                        ariaLabel={label}
                    />
                </div>
                {isSaving && <Spinner size="normal" />}
            </div>
        );
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontWeight: "bold", minWidth: "120px" }}>{label}</label>
            <span>{value || "-"}</span>
            {!disabled && (
                <button
                    onClick={enterEditMode}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                    }}
                    aria-label={`Edit ${label}`}
                >
                    <Icon name="edit" />
                </button>
            )}
        </div>
    );
};

export default InlineSelectField;
